<template>
  <div
    class="mm-promo"
    :class="{
      'mm-promo--tablet-fit': tabletFluid,
      'mm-promo--tablet-stacked': isTablet,
      'mm-promo--wrap': parentColumnCount >= 3,
      'mm-promo--zero-col': parentColumnCount === 0
    }"
    :style="rootStyle"
  >
    <div class="mm-promo__heading">
      <span v-if="promo.header" class="mm-promo__heading-text">
        {{ promo.header }}
        <span class="mm-promo__rule" />
      </span>
    </div>

    <div class="mm-promo__grid" :style="gridStyle">
      <component
        :is="tile.url && !inert ? 'a' : 'div'"
        v-for="(tile, index) in promo.tiles"
        :key="index"
        :href="tile.url && !inert ? tile.url : null"
        class="mm-tile"
        @click="onNavigate($event, tile)"
        :class="{
          'mm-tile--inline': effectiveTileLayout === 'inline',
          'mm-tile--fit': fitsInlineCaptions
        }"
        :style="inlineShareStyle"
      >
        <div class="mm-tile__frame" :style="frameStyle">
          <img
            v-if="tileImage(tile)"
            :src="tileImage(tile)"
            :alt="tile.header || ''"
            class="mm-tile__img"
            :style="imageStyle(tile)"
            loading="lazy"
          >
          <!-- No image on this tile: fall back to the same "no image available"
               graphic the product grid serves, rather than a blank grey box. -->
          <img
            v-else
            :src="placeholderImage"
            alt=""
            class="mm-tile__img mm-tile__img--placeholder"
            loading="lazy"
          >
        </div>

        <div class="mm-tile__body">
          <!-- A header-less tile still reserves the header line when another
               tile in its row has one, so every caption in the row starts at
               the same height (client review). -->
          <p
            v-if="tile.header || reserveHeader(index)"
            class="mm-tile__header"
            v-tippy="tile.header ? truncationTip(tile.header) : false"
          >{{ tile.header }}</p>
          <p
            v-if="tile.text"
            class="mm-tile__text"
            v-tippy="truncationTip(tile.text)"
          >{{ tile.text }}</p>
        </div>
      </component>
    </div>
  </div>
</template>

<script>
import { followInternalLink } from '~/helpers/internal-link'
import { isTabletWidth, sharesRowWithColumns } from '~/helpers/mega-menu-tablet'

/**
 * Promo pane. Shared by the public header and the admin preview.
 *
 * Every tile is a FIXED-ratio box (4:3 or 3:4, per the preset) — the frame owns
 * the aspect via CSS `aspect-ratio`, so all tiles in a pane are identical
 * regardless of image shape or caption length. The earlier version derived the
 * frame height from the leftover cell space under a variable caption, which
 * made images different heights and stretched them off-ratio (client review
 * "Image Ratio and Pane Consistency").
 *
 * The stored crop box is a source-pixel rectangle in the SAME aspect as the
 * slot; the bytes on S3 are never re-encoded, so a crop stays re-editable. It is
 * applied here as a uniform scale + translate (imageStyle) so the box exactly
 * fills the frame — a plain object-position only pans and cannot zoom into a box.
 */

// The "no image available" graphic the product grid falls back to
// (GridView.vue). Reused so an empty promo tile matches the rest of the site.
const PLACEHOLDER_IMAGE = '/img/images/image_not_available.png'

/* Pane geometry, so the largest tile that fits BOTH the pane width (across
   columns) and the pane height (across rows) can be picked while every tile
   keeps its fixed ratio. These mirror the .mm-promo padding / heading / gap /
   body values in the stylesheet below; keep them in step. */
const PANE_PAD_TOP = 25
const PANE_PAD_BOTTOM = 20
const PANE_PAD_SIDE = 20
// Rendered heading block: the header text (20px) + the rule (margin 6 + 1px) is
// 27px, above the 26px min-height, plus the 20px margin-bottom = 47. Reserving
// 46 under-counted by 1px, which pushed height-constrained (portrait) presets a
// hair over the pane and tripped the overflow scrollbar (client QA).
const HEADING_BLOCK = 47
// A couple of px of slack so sub-pixel rounding — the frame height derived from
// aspect-ratio, or the preview's scale transform — can never tip a tile that
// mathematically fits into the overflow scrollbar.
const FIT_SAFETY = 6
// Row gap is deliberately larger than the column gap so each image + its caption
// reads as one group, clearly separated from the tile below — otherwise the
// caption floats midway and looks like it belongs to the next image (client
// review). Keep these in step with column-gap / row-gap in the stylesheet.
const COL_GAP = 26
// Column-gap on .mm-promo__grid for the roomy 3-column inline tile.
const ROOMY_GAP = 36
const ROOMY_ROW_GAP = 60
const ROW_GAP = 25
// Desktop 12-tile (4x3) preset with no columns: wider gap between its three rows.
const P0_12_ROW_GAP = 50
// Caption block: header margin 5 + header line 15 + two text lines (2*15, less
// the -2 tuck) = 48. Matches .mm-tile__body min-height; keep the two in step.
const BODY_RESERVE = 48
const MIN_TILE_PX = 60
// .mm-promo__grid owns the vertical scrollbar (overflow-y: auto). When the
// tile grid is taller than the pane cap (e.g. 0-col preset p0_8 lands 2 rows
// of 4:3 landscape tiles that exceed the 580 max-height), the scrollbar
// eats horizontal grid area — 6px on webkit, ~8 elsewhere. Subtract this
// from the track math for the desktop 0-col branch so the last column never
// clips against the pane's overflow: hidden edge when it appears.
const SCROLLBAR_W = 8
// Wrapped tablet pane: the panel inner's side padding and the pane's own padding
// (.mm-promo--tablet-stacked). Keep in step with the stylesheets.
const TABLET_INNER_PAD = 20
const TABLET_PANE_PAD = 10
const TABLET_PANE_PAD_Y = 20
// Wrapped tablet panel scrolls anyway, so a short viewport never shrinks tiles
// below this just to fit every row in view.
const TABLET_MIN_TILE_PX = 200

export default {
  name: 'MegaMenuPromoPane',

  props: {
    promo: {
      type: Object,
      required: true
    },
    width: {
      type: Number,
      default: null
    },
    /**
     * Height of the whole menu panel, so tiles can be sized to fit the pane
     * height as well as its width.
     */
    paneHeight: {
      type: Number,
      default: 580
    },
    /** [columns, rows] from the chosen layout preset. */
    grid: {
      type: Array,
      default: () => [1, 1]
    },
    /**
     * Tile orientation for the chosen preset: '4:3' landscape or '3:4'
     * portrait. Magnitude is always 4:3; only the orientation changes.
     */
    ratio: {
      type: String,
      default: '4:3'
    },
    /**
     * 'stacked' (image above header + text) or 'inline' (image beside them).
     * Two 0-column presets are drawn inline in the XD; every other preset stacks.
     */
    tileLayout: {
      type: String,
      default: 'stacked'
    },
    /**
     * Count of columns in the parent MegaMenuPanel. At tablet, 1-2 col menus
     * share their row with the column block; 3+ col menus wrap the promo pane
     * to its own row so tiles use the full pane width.
     */
    parentColumnCount: {
      type: Number,
      default: 0
    },
    inert: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      placeholderImage: PLACEHOLDER_IMAGE,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1440,
      renderedWidth: 0
    }
  },

  mounted () {
    window.addEventListener('resize', this.onResize)

    // Pane grows with the viewport under flex-grow proportional to the matrix
    // width. Tile tracks need to react to the ACTUAL rendered pane width, not
    // the matrix fallback — otherwise images stay at their 1440-reference size
    // on wider screens. ResizeObserver pushes the real px width into gridStyle.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(entries => {
        const w = entries[0]?.contentRect?.width || 0
        if (w) this.renderedWidth = w
      })
      this.resizeObserver.observe(this.$el)
    }

    this.$nextTick(this.fitInlineCaptions)
  },

  updated () {
    this.fitInlineCaptions()
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },

  computed: {
    /**
     * Pane grows and shrinks with the viewport in a strict matrix-width ratio
     * against the column block. flex-grow equal to the matrix width (basis 0)
     * means both children divide the inner's full width proportionally.
     */
    rootStyle () {
      // 0-column menus stack the promo pane inside a flex-COLUMN inner (no
      // sibling column block to share horizontal width with). The matrix
      // flex uses `flex-basis: 0`, which in a column context contributes 0
      // to the pane's intrinsic height — so the auto-height panel collapses.
      // Drop the flex rule for this case so promo keeps its natural content
      // height and the panel can shrink to fit it.
      if (this.parentColumnCount === 0 || !this.width) {
        return { width: '100%' }
      }

      return {
        width: '100%',
        flex: this.width + ' 1 0',
        minWidth: 0
      }
    },

    columns () {
      return Math.max(1, Number(this.grid[0]) || 1)
    },

    rows () {
      return Math.max(1, Number(this.grid[1]) || 1)
    },

    /** Numeric aspect of the slot, width / height. */
    aspect () {
      const [rw, rh] = String(this.ratio).split(':').map(Number)

      return rw && rh ? rw / rh : 4 / 3
    },

    /** Same aspect as a CSS `aspect-ratio` value, e.g. '4 / 3'. */
    aspectCss () {
      const [rw, rh] = String(this.ratio).split(':').map(Number)

      return rw && rh ? `${rw} / ${rh}` : '4 / 3'
    },

    /**
     * Width of a single tile track — the largest that lets the grid fit inside
     * the pane both across its columns and, with the fixed aspect and a reserved
     * caption block, down its rows. The frame then derives its height from this
     * width via aspect-ratio, so tiles can never come out off-ratio or unequal.
     * Returned as a fixed track width so the grid CENTRES the tiles rather than
     * stretching them across the pane, matching every XD preset.
     */
    tileWidthPx () {
      const gridH = this.paneHeight - PANE_PAD_TOP - PANE_PAD_BOTTOM - HEADING_BLOCK - FIT_SAFETY
      const rowH = (gridH - ROW_GAP * (this.rows - 1)) / this.rows
      const frameH = Math.max(MIN_TILE_PX / this.aspect, rowH - BODY_RESERVE)
      const widthByHeight = frameH * this.aspect

      const paneW = this.renderedWidth || this.width || widthByHeight * this.columns
      const available = paneW - 2 * PANE_PAD_SIDE
      const widthByWidth = (available - COL_GAP * (this.columns - 1)) / this.columns

      // Floor, never round: a tile rounded UP re-inflates the height it was just
      // sized to fit and brings the scrollbar back for portrait presets.
      return Math.max(MIN_TILE_PX, Math.floor(Math.min(widthByHeight, widthByWidth)))
    },

    /**
     * Stacked presets get fixed aspect-derived tile tracks and the grid is
     * centred, matching the XD. Inline presets (0-column, image-beside-caption)
     * span the pane, so they keep equal-share 1fr columns.
     */
    /**
     * Tablet range (768-1279): switch to 1fr tracks so tiles share the pane
     * width and cannot overflow the flex-shrunk pane.
     */
    isTablet () {
      // Tablet menus whose pane shares a row with the columns are sized by the
      // desktop branches below; only the wrapped ones (3-4 columns, narrow)
      // use the tablet branch.
      return this.isTabletRange && !this.tabletFluid
    },

    /** Wrapped tablet pane of a 3- or 4-column menu with the 2x2 (4-tile) preset. */
    isWrappedQuad () {
      return this.isTablet && [3, 4].includes(this.parentColumnCount) && this.promo?.tiles?.length === 4
    },

    isTabletRange () {
      return isTabletWidth(this.windowWidth)
    },

    tabletFluid () {
      return this.isTabletRange && sharesRowWithColumns(this.parentColumnCount, this.windowWidth)
    },

    /**
     * 0-column (promo-only) menus force stacked at every viewport: header + text
     * sit BELOW the image. Inline captions on col=0 mixed image and caption
     * heights across the row, so a shorter caption's tile ended up rendering
     * with row-1 body dropping into row-2's image lane (client QA).
     *
     * Exception: p0_6 (6 tiles, 2x3) and p0_12 (12 tiles, 4x3) are laid out
     * inline — caption on the right of the image (client request). The
     * row-alignment issue cited above does not bite these presets because
     * grid-auto-rows: max-content + fixed track widths keep each row an
     * independent lane. p0_14 stays stacked (caption below image) so its
     * 7-wide row can use the full track width for the frame.
     */
    effectiveTileLayout () {
      const tileCount = this.promo?.tiles?.length || 0

      // Tablet: four tiles across leave each one too narrow for the caption
      // beside the image, so the 12-tile preset puts it underneath.
      if (this.isTabletRange && this.parentColumnCount === 0 && tileCount === 12) return 'stacked'

      if (this.parentColumnCount === 0 && this.tileLayout === 'inline') {
        if (tileCount === 6 || tileCount === 12) return 'inline'
        return 'stacked'
      }

      // A wrapped tablet pane spans the whole panel, so the 1x2 preset of 3- and
      // 4-column menus has the room for the caption beside the image, as on desktop.
      if (this.isTablet && this.columns === 1 && this.rows === 2) return 'inline'

      // Same for the 2x2 preset of 3- and 4-column menus: the wide wrapped pane gives each
      // of the four tiles room for the caption at the top right of its image.
      if (this.isWrappedQuad) return 'inline'

      // Tablet panes that share their row are too narrow for the caption beside the image.
      if (this.isTabletRange) return this.tileLayout

      // Desktop: the caption goes top right of the image on the 1x2 (4:3) preset
      // of 2- and 3-column menus, and on the 2x2 preset of 1- and 2-column menus.
      // The 3-column 2x2 stays stacked. Keyed by the resolved grid, not the
      // preset slug, so the admin preview routes the same way.
      if (!this.isTablet && this.rows === 2 && (
        ([2, 3].includes(this.parentColumnCount) && this.columns === 1) ||
        ([1, 2].includes(this.parentColumnCount) && this.columns === 2)
      )) {
        return 'inline'
      }

      return this.tileLayout
    },

    /**
     * Share of an inline tile's width taken by the image. 2- and 3-column menus
     * have a narrow pane, so their image gets more than the standard 40% or it
     * shrinks to a thumbnail.
     */
    inlineFrameShare () {
      // The caption needs real width to read, so this preset gives it half the tile.
      if (this.isWrappedQuad) return 0.5

      return [1, 2, 3].includes(this.parentColumnCount) || this.isTablet ? 0.6 : 0.4
    },

    /** Inline tiles: the caption is fitted to the image height. */
    fitsInlineCaptions () {
      return this.effectiveTileLayout === 'inline'
    },

    /** The inline tiles that get the roomy treatment: wide gutters, caption fitted to the image. */
    isRoomyInline () {
      return this.effectiveTileLayout === 'inline' && this.rows === 2 &&
        ([1, 2, 3].includes(this.parentColumnCount) || this.isTablet)
    },

    inlineShareStyle () {
      return this.effectiveTileLayout === 'inline'
        ? { '--mm-frame-share': `${this.inlineFrameShare * 100}%` }
        : null
    },

    /**
     * Frame keeps the preset aspect at every viewport; its width comes from the
     * grid track (see gridStyle).
     */
    frameStyle () {
      return { aspectRatio: this.aspectCss }
    },

    gridStyle () {
      // Desktop 0-column (promo-only) menus: tiles grow with the pane width
      // and shrink to fit the height cap, so the pane keeps every tile inside
      // the window (no horizontal scroll, no clip). Grid rows are auto — each
      // row = frame height + caption — so a shorter caption never lets its
      // body drop into the following row's image lane (client QA). 6-tile
      // preset widens the column gap so image columns read as clearly
      // separated groups.
      if (!this.isTablet && this.parentColumnCount === 0) {
        const tileCount = this.promo?.tiles?.length || 0
        const colGap = tileCount === 6 ? 80 : COL_GAP
        // Desktop only: this branch also sizes the tablet 0-column menus.
        const wideRows = tileCount === 12 && !this.isTabletRange
        const rowGap = wideRows ? P0_12_ROW_GAP : ROW_GAP
        // Pane now fills the inner with no gutter and inner has no max-width;
        // use the rendered pane width when available (via ResizeObserver),
        // falling back to the viewport for the first paint.
        const panelInner = this.renderedWidth || this.windowWidth
        const gridArea = panelInner - 2 * PANE_PAD_SIDE - SCROLLBAR_W
        const perCol = Math.floor((gridArea - colGap * (this.columns - 1)) / this.columns)
        // Viewport-height cap: with the panel pinned at 82vh on desktop, size
        // tiles so rows × (frame + caption) + row gaps fit within the pane
        // instead of overflowing into the grid's internal scrollbar.
        const gridH = this.paneHeight - PANE_PAD_TOP - PANE_PAD_BOTTOM - HEADING_BLOCK - FIT_SAFETY
        const rowH = (gridH - rowGap * (this.rows - 1)) / this.rows
        // Inline tiles (p0_6) put the caption BESIDE the image, so the body
        // block is center-aligned next to the frame and does not consume
        // vertical space under it. Drop BODY_RESERVE for that layout and
        // scale widthByHeight by the frame's 40% share of the track so the
        // frame hits aspect height within rowH. Stacked tiles reserve the
        // caption as it really renders: the flat 48 under-counts a header +
        // 2-line description, so once the rows turn height-bound (p0_14 from
        // ~1600px wide) the bottom row was cut off by the hidden overflow.
        const isInline = this.effectiveTileLayout === 'inline'
        const bodyReserve = isInline ? 0 : Math.max(BODY_RESERVE, this.captionHeight())
        const frameFraction = isInline ? 0.4 : 1
        const frameH = Math.max(MIN_TILE_PX / this.aspect, rowH - bodyReserve)
        const widthByHeight = (frameH * this.aspect) / frameFraction
        // Tiles grow with the viewport (perCol follows the pane width) and are
        // bounded by widthByHeight, so they never push past the panel height.
        const widthCap = Math.min(perCol, widthByHeight, this.sparseTileMaxPx(tileCount))
        // Extra 10px slack per tile so the last column keeps clear of the
        // pane's overflow: hidden edge — chrome varies (scrollbar width, panel
        // border, sub-pixel rounding across zoom levels) and even with the
        // math fitting exactly the browser occasionally clipped the right
        // tile at fractional device pixel ratios. p0_14 skips the slack so
        // its 7 tracks still read as filling the window even though the
        // widthByHeight cap keeps them inside the panel height.
        const slack = tileCount === 14 ? 0 : 10
        const trackW = Math.max(MIN_TILE_PX, widthCap - slack)
        // The pane spans the whole panel, so height-bound presets leave white
        // space either side of the tiles; the gutter soaks it up (capped).
        const fillGap = this.columns > 1
          ? Math.floor(Math.min(140, Math.max(colGap, (gridArea - this.columns * trackW) / (this.columns - 1))))
          : colGap

        return {
          '--mm-cols': this.columns,
          gridTemplateColumns: `repeat(${this.columns}, ${trackW}px)`,
          gridAutoRows: 'max-content',
          columnGap: `${fillGap}px`,
          ...(wideRows && { rowGap: `${rowGap}px` }),
          width: '100%',
          justifyContent: 'center',
          alignContent: 'start'
        }
      }

      // Desktop 1- / 2- / 3- / 4-column + promo (800 / 660 / 460 / 340 pane).
      // Same designed-size treatment as the 0-column branch: track fixed at
      // 250 portrait / 350 landscape so the tile block fills the pane width
      // instead of leaving huge left/right gutters around a height-shrunk tile
      // (client QA — "extra space from left and right on the desktop of promo
      // pane"). Presets whose designed rows exceed the 580px pane (p1_4/6/8,
      // p2_2_stk, p2_4/6, p3_2_stk, p3_4, p4_2_stk) scroll inside the grid via
      // overflow-y:auto, the documented fallback. Narrow panes (3- and 4-col)
      // shrink the track proportionally so no tile clips the pane edge.
      if (!this.isTablet && this.parentColumnCount > 0) {
        // Pane now flexes with the viewport, so prefer the real rendered width.
        // Falls back to the matrix value or a column-count default so first
        // paint (before ResizeObserver fires) never divides by 0.
        const paneFallback = { 1: 800, 2: 660, 3: 460, 4: 340 }
        const paneW = this.renderedWidth || this.width || paneFallback[this.parentColumnCount] || 800
        const gridArea = paneW - 2 * PANE_PAD_SIDE - SCROLLBAR_W
        // Multi-column presets with 2/4/6/8 tiles get a wider gutter so the image
        // columns read as separate groups. The gutters share a fixed slice of the
        // pane width (16%), so a two-column preset keeps a generous gap while a
        // wider one does not spend the width its tiles need. Other tile counts
        // keep the standard gap. Same rule for every column count.
        // The 3-column 2x2, 2-column 3x2 and 1-column 3x2 / 4x2 have just enough
        // pane for their tiles: every px of width goes to the image tracks and
        // the gutter then soaks up what is left.
        const tilesTotal = this.promo?.tiles?.length
        const tightPane = (this.parentColumnCount === 3 && tilesTotal === 4) ||
          (this.parentColumnCount === 2 && tilesTotal === 6) ||
          (this.parentColumnCount === 1 && [6, 8].includes(tilesTotal))
        const wideGap = !tightPane && this.effectiveTileLayout !== 'inline' && this.columns > 1 &&
          [2, 4, 6, 8].includes(this.promo?.tiles?.length)
        // Inline 3-column tiles are width-starved, so their gutter is tightened.
        const colGap = wideGap
          ? Math.round(Math.min(140, Math.max(COL_GAP, (gridArea * 0.16) / (this.columns - 1))))
          : (this.effectiveTileLayout === 'inline' && [1, 2, 3].includes(this.parentColumnCount) ? (this.isRoomyInline ? ROOMY_GAP : 16) : COL_GAP)
        const perCol = Math.floor((gridArea - colGap * (this.columns - 1)) / this.columns)
        // Viewport-height cap: panel is pinned at 82vh on desktop, so size
        // tiles against the live pane height. Shorter viewports shrink tiles
        // to fit instead of overflowing into the grid's internal scrollbar.
        // 1-column multi-tile presets (p1_4/p1_6/p1_8) are tall-row inside
        // the 82vh cap, so BODY_RESERVE=48 ate too much vertical budget and
        // starved widthByHeight — tiles stayed narrow and the pane showed
        // large side gutters even at wide viewports. Drop the body reserve
        // to 15 (header line only; description still rendered but min-height
        // relaxed) for this case so the row can give widthByHeight more
        // room; a matching --mm-body-min CSS var reduces the body's
        // min-height so DOM layout agrees with the math.
        const tileCount = this.promo?.tiles?.length || 0
        // Compact-body: reduce caption reserve from 48 → 15 for multi-tile
        // presets where multiple tile columns make per-tile captions short.
        // Applies to 1-col menus (original) and 2-col multi-tile (e.g. p2_6
        // 3×2) — more row height means widthByHeight cap is higher, so tiles
        // can grow wider with the viewport before hitting the height ceiling.
        const compactBody = tileCount > 1 &&
          (this.parentColumnCount === 1 || (this.parentColumnCount === 2 && this.columns > 1))
        // Inline tiles (caption beside image, e.g. p2_2_stk / p2_4): drop
        // bodyReserve to 0 and scale widthByHeight by the frame's 40% share
        // of the track (same treatment as the 0-col inline branch above).
        const isInline = this.effectiveTileLayout === 'inline'
        const bodyReserve = isInline ? 0 : Math.max(compactBody ? 15 : BODY_RESERVE, this.captionHeight())
        const frameFraction = isInline ? this.inlineFrameShare : 1
        // Small safety margin under the tiles, scaled with the pane height. Kept
        // tight because multi-tile presets are height-bound: every spare px here
        // is a px less on the tile size.
        const edgeGap = Math.round(Math.min(14, Math.max(FIT_SAFETY, this.paneHeight * 0.012)))
        const gridH = this.paneHeight - PANE_PAD_TOP - PANE_PAD_BOTTOM - HEADING_BLOCK - edgeGap
        const rowGap = this.isRoomyInline ? ROOMY_ROW_GAP : ROW_GAP
        const rowH = (gridH - rowGap * (this.rows - 1)) / this.rows
        const frameH = Math.max(MIN_TILE_PX / this.aspect, rowH - bodyReserve)
        const widthByHeight = (frameH * this.aspect) / frameFraction
        // Same rule as the 0-column branch: grow with the pane width, bounded by
        // the height cap.
        // The roomy 1x2 inline preset (caption beside the image) fills the pane
        // width instead of taking the 1-2 tile cap.
        const fillsPane = this.isRoomyInline
        const widthCap = Math.min(perCol, widthByHeight, fillsPane ? Infinity : this.sparseTileMaxPx(tileCount))
        const trackW = Math.max(MIN_TILE_PX, widthCap - (tightPane ? 0 : 10))

        // Tight-pane 2x2: the tiles keep their size and the gutter soaks up
        // whatever width is left in the pane, so no white space is left over.
        const gap = tightPane
          ? Math.floor(Math.min(140, Math.max(COL_GAP, (gridArea - this.columns * trackW) / (this.columns - 1))))
          : colGap

        const style = {
          '--mm-cols': this.columns,
          gridTemplateColumns: `repeat(${this.columns}, ${trackW}px)`,
          gridAutoRows: 'max-content',
          columnGap: `${gap}px`,
          rowGap: `${rowGap}px`,
          width: '100%',
          justifyContent: 'center',
          alignContent: 'start'
        }
        if (compactBody) style['--mm-body-min'] = '15px'
        return style
      }

      // Tablet 3- and 4-column + promo on a narrow tablet: the pane wraps onto
      // its own full-width row under the columns. Sized by the desktop rules:
      // tiles fill the live pane width, bounded so the whole promo fits the
      // visible panel once it is scrolled into view, and the gutter soaks up
      // the width the tiles leave so no empty strip sits either side.
      if (this.isTablet) {
        // renderedWidth is the pane's content box (inside its side padding).
        const gridArea = this.renderedWidth || this.windowWidth - 2 * (TABLET_INNER_PAD + TABLET_PANE_PAD)
        const isInline = this.effectiveTileLayout === 'inline'
        // The final gap below soaks up whatever width the tiles leave, so the tracks
        // only reserve a modest one when the caption needs the width.
        const colGap = this.isWrappedQuad
          ? ROOMY_GAP
          : this.columns > 1
            ? Math.round(Math.min(140, Math.max(COL_GAP, (gridArea * 0.16) / (this.columns - 1))))
            : COL_GAP
        const perCol = Math.floor((gridArea - colGap * (this.columns - 1)) / this.columns)

        const bodyReserve = isInline ? 0 : Math.max(BODY_RESERVE, this.captionHeight())
        const frameFraction = isInline ? this.inlineFrameShare : 1
        const edgeGap = Math.round(Math.min(14, Math.max(FIT_SAFETY, this.paneHeight * 0.012)))
        const gridH = this.paneHeight - 2 * TABLET_PANE_PAD_Y - HEADING_BLOCK - edgeGap
        const rowGap = this.isRoomyInline ? ROOMY_ROW_GAP : ROW_GAP
        const rowH = (gridH - rowGap * (this.rows - 1)) / this.rows
        const frameH = Math.max(MIN_TILE_PX / this.aspect, rowH - bodyReserve)
        const widthByHeight = (frameH * this.aspect) / frameFraction

        // The 1x2 inline preset fills the pane like its desktop counterpart. The
        // single-tile hero keeps the size the client signed off for tablet ("3-col
        // and 4-col with one tile promo pane show huge"); the rest take the
        // desktop sparse-tile cap.
        const tileCount = this.promo?.tiles?.length || 0
        const sizeCap = isInline
          ? Infinity
          : (tileCount <= 1 ? (this.aspect < 1 ? 339 : 350) : this.sparseTileMaxPx(tileCount))
        const fitted = Math.max(Math.min(perCol, TABLET_MIN_TILE_PX), Math.min(perCol, widthByHeight))
        const trackW = Math.max(MIN_TILE_PX, Math.min(fitted, sizeCap) - 10)
        const gap = this.columns > 1
          ? Math.floor(Math.min(140, Math.max(colGap, (gridArea - this.columns * trackW) / (this.columns - 1))))
          : colGap

        return {
          '--mm-cols': this.columns,
          '--mm-tile-w': `${trackW}px`,
          gridTemplateColumns: `repeat(${this.columns}, ${trackW}px)`,
          columnGap: `${gap}px`,
          rowGap: `${rowGap}px`,
          width: '100%',
          justifyContent: 'center',
          alignContent: 'start'
        }
      }

      if (this.tileLayout === 'inline') {
        return {
          '--mm-cols': this.columns,
          gridTemplateColumns: `repeat(${this.columns}, minmax(0, 1fr))`,
          alignContent: 'center'
        }
      }

      return {
        '--mm-cols': this.columns,
        gridTemplateColumns: `repeat(${this.columns}, ${this.tileWidthPx}px)`,
        justifyContent: 'center',
        alignContent: 'center'
      }
    }
  },

  methods: {
    onResize () {
      this.windowWidth = window.innerWidth
    },

    // Tiles without a url render as a div and the helper ignores them.
    onNavigate (event, tile) {
      if (this.inert || !tile.url) return

      followInternalLink(event, this.$router)
      this.$emit('navigate', tile)
    },

    /**
     * Desktop inline tiles: keep the caption inside the image height. The title
     * takes the lines it needs (capped to the tile), the description gets the
     * lines left over, and anything longer ends in an ellipsis.
     */
    fitInlineCaptions () {
      if (!this.fitsInlineCaptions) {
        this.$el.querySelectorAll('.mm-tile__header, .mm-tile__text').forEach(el => {
          el.style.removeProperty('-webkit-line-clamp')
          el.style.removeProperty('display')
        })
        return
      }

      const lineCount = (el, line) => Math.max(1, Math.round(el.scrollHeight / line))

      this.$el.querySelectorAll('.mm-tile--fit').forEach(tile => {
        const frame = tile.querySelector('.mm-tile__frame')
        const header = tile.querySelector('.mm-tile__header')
        const text = tile.querySelector('.mm-tile__text')
        const room = frame.getBoundingClientRect().height
        let used = 0

        if (header) {
          header.style.setProperty('-webkit-line-clamp', 'unset')
          const style = getComputedStyle(header)
          const line = parseFloat(style.lineHeight) || 15
          const margin = parseFloat(style.marginTop) || 0
          const lines = Math.max(1, Math.min(lineCount(header, line), Math.floor((room - margin) / line)))

          header.style.setProperty('-webkit-line-clamp', String(lines))
          used = margin + lines * line
        }

        if (text) {
          text.style.display = ''
          text.style.setProperty('-webkit-line-clamp', 'unset')
          const style = getComputedStyle(text)
          const line = parseFloat(style.lineHeight) || 15
          const margin = parseFloat(style.marginTop) || 0
          const lines = Math.floor((room - used - margin) / line)

          if (lines < 1) {
            text.style.display = 'none'
          } else {
            text.style.setProperty('-webkit-line-clamp', String(lines))
          }
        }
      })
    },

    /**
     * Caption block as it really renders: header line (5 margin + 15) plus a
     * description clamped to two lines (5 margin + 30). The flat 48 under-counted
     * a header + 2-line description by ~7px, so that tile ran past the bottom of
     * the panel. Tablet's third description line is held back by the panel.
     */
    captionHeight () {
      const tiles = this.promo?.tiles || []

      return (tiles.some(t => t.header) ? 20 : 0) + (tiles.some(t => t.text) ? 35 : 0)
    },

    /**
     * One- and two-tile promos have the whole pane to themselves, so left to
     * the pane/height limits they balloon. Cap their width at a size that reads
     * as a feature image, growing gently with the viewport (reference 1440px).
     * A single row of three, or of four portrait tiles, is capped like the
     * two-tile promos. Anything else is sized by the pane alone.
     */
    sparseTileMaxPx (tileCount) {
      const portrait = this.aspect < 1
      const shortRow = this.rows === 1 && (tileCount === 3 || (tileCount === 4 && portrait))

      if (tileCount > 2 && !shortRow) return Infinity

      const base = tileCount <= 1 ? (portrait ? 300 : 400) : (portrait ? 250 : 340)
      // The wrapped tablet pane is wider than any desktop pane beside columns, so
      // it keeps the reference size rather than scaling down with the viewport.
      const scale = this.isTablet ? 1 : Math.min(1.35, Math.max(0.85, this.windowWidth / 1440))

      return Math.round(base * scale)
    },

    /** Tooltip that only opens when the caption is actually cut off (the "..."). */
    truncationTip (content) {
      return {
        content,
        placement: 'top',
        onShow: ({ reference }) =>
          reference.scrollWidth > reference.clientWidth ||
          reference.scrollHeight > reference.clientHeight
      }
    },

    /**
     * A tile whose image was deleted renders its text and a placeholder rather
     * than breaking.
     */
    tileImage (tile) {
      if (!tile.image) return null

      return tile.image.url || null
    },

    /**
     * True when this tile's ROW contains at least one header, so a header-less
     * tile keeps the reserved header line and captions across the row stay
     * aligned. Tiles flow row-major into `columns` tracks.
     */
    reserveHeader (index) {
      // Row grouping must match the currently rendered track count, so header
      // alignment stays correct when tablet overrides the preset's grid (e.g.
      // p0_14 renders 6-per-row at 768-1279 instead of 7).
      const tileCount = this.promo?.tiles?.length || 0
      const cols = this.isTablet && this.parentColumnCount === 0 && tileCount === 14
        ? 7
        : this.columns
      const start = Math.floor(index / cols) * cols

      return this.promo.tiles
        .slice(start, start + cols)
        .some(tile => tile.header)
    },

    /**
     * Apply the stored crop as a uniform scale + translate so the crop box
     * exactly fills the frame. The box shares the frame's aspect, so this never
     * distorts — it only zooms and pans to the region the admin chose. Without
     * the image's natural dimensions (older crops) it falls back to object-fit
     * cover, which is the class default.
     */
    imageStyle (tile) {
      const crop = tile.image && tile.image.crop

      if (!crop || !crop.width || !crop.height || !crop.iw || !crop.ih) return null

      return {
        width: (crop.iw / crop.width) * 100 + '%',
        height: (crop.ih / crop.height) * 100 + '%',
        left: (-crop.x / crop.width) * 100 + '%',
        top: (-crop.y / crop.height) * 100 + '%',
        right: 'auto',
        bottom: 'auto',
        objectFit: 'fill'
      }
    }
  }
}
</script>

<style scoped lang="scss">
.mm-promo {
  /* Full height of the 580px panel (the panel stretches its flex children), a
     flex column so the heading takes its natural height and the grid fills the
     rest. Everything is then bounded by the pane and cannot spill past the
     fold. Desktop keeps the default flex (0 1 auto) so the pane absorbs any
     sub-1440 shortfall the way the XD expects; the 768-1279 media query below
     pins the pane to a viewport-proportional width for the tablet mock. */
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 25px 5px 20px;
  box-sizing: border-box;
}

/* Tablet range (768-1279). A pane that shares its row with the columns
   (.mm-promo--tablet-fit) keeps the base styles and the flex ratio from
   rootStyle, exactly like desktop; its tiles are sized from the live pane width
   and the panel height cap. A wrapped pane (.mm-promo--tablet-stacked, 3-4
   columns on a narrow tablet) takes a full row of its own and its natural
   height, and the panel scrolls as one unit instead of the grid trapping a
   scrollbar. */
@media (min-width: 768px) and (max-width: 1279px) {
  .mm-promo--tablet-stacked {
    flex: 1 1 100% !important;
    width: auto !important;
    height: auto !important;
    min-width: 360px !important;
    max-width: none !important;
    padding: 20px 10px !important;
    overflow-x: hidden !important;
  }

  /* Gaps come from gridStyle. */
  .mm-promo--tablet-stacked .mm-promo__grid {
    flex: 0 0 auto;
    overflow: visible;
    grid-auto-rows: min-content !important;
    align-items: start !important;
  }

  .mm-promo--tablet-stacked .mm-tile {
    width: var(--mm-tile-w, 100%);
    min-width: 0;
    max-width: 100%;
    justify-self: center;
  }

  /* Width from the --mm-tile-w track set by gridStyle; height follows from
     the inline aspect-ratio. */
  .mm-promo--tablet-stacked .mm-tile__frame {
    width: var(--mm-tile-w, 100%);
    max-width: 100%;
  }

  /* Same no-scroll grid as desktop: the tile math fits tiles inside the cap. */
  .mm-promo--tablet-fit .mm-promo__grid {
    overflow-y: hidden;
    overscroll-behavior: auto;
  }

  /* Caption treatment like desktop (see the 1280 block below), but with a
     three-line description on tablet: a single-line title and a clamped
     description, so a long caption cannot grow its tile and break the row
     alignment. TABLET_DESCRIPTION_LINES in MegaMenuPanel reserves the room. */
  .mm-tile__header {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: auto;
  }

  .mm-tile--fit .mm-tile__header,
  .mm-tile--fit .mm-tile__text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mm-tile__text {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
  }
}

.mm-promo__heading {
  /* flex + centre rather than text-align: an inline-block child would add
     baseline leading, so a blank header would reserve a different height than a
     filled one. See the note in MegaMenuColumn.vue. */
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  align-items: flex-start;
  min-height: 26px;
  margin-bottom: 20px;
}

.mm-promo__heading-text {
  /* Shrink-wrapped so the rule is 60% of the TEXT, not of the pane. Measured on
     the XD "Design Instructions" promo header at 9x: 980 units of text, 587 of
     rule (59.9%), inset 198 left and 195 right. */
  display: block;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 20px;
  line-height: 1;
  text-transform: uppercase;
  color: #383838;
}

.mm-promo__rule {
  display: block;
  width: 60%;
  height: 1px;
  margin: 6px auto 0;
  background-color: #383838;
}

.mm-promo__grid {
  display: grid;
  /* Row gap > column gap: binds each image to its caption and separates it from
     the tile below (client review). Keep in step with COL_GAP / ROW_GAP above. */
  column-gap: 26px;
  row-gap: 25px;
  /* Claim the height left under the heading and stay inside it — min-height:0
     lets a grid child shrink below its content so the tiles are bounded by the
     pane. overflow-y:auto is the safety net: a preset with more tile rows than
     the 580px pane can hold at a legible size scrolls rather than clipping, the
     same behaviour the columns have. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Per CSS spec, once one axis is non-visible the other cannot stay visible —
     it silently promotes to auto. That silently added a horizontal scrollbar
     whenever a track math rounding error pushed content 1-2px past the pane
     inner width. Pin overflow-x hidden so the pane never scrolls sideways. */
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #cfcfcf;
    border-radius: 3px;
  }
}

/* Desktop: no internal promo grid scroll — wheel events bubble to the page
   (Neiman Marcus behavior). Tile-sizing math already fits tiles within 82vh. */
@media (min-width: 1280px) {
  .mm-promo__grid {
    overflow-y: hidden;
    overscroll-behavior: auto;
  }
}

/* Image left, header and text to its right — XD 0-column presets p0_6 and
   p0_12. The frame keeps its aspect box; only the flow changes.
   Double-class selector boosts specificity above the sibling .mm-tile base
   rule below so flex-direction: row wins (otherwise same-specificity + later
   source position let the base's `column` reset the inline flow). */
.mm-tile.mm-tile--inline {
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;

  .mm-tile__frame {
    /* Width, not height: the frame is the left 40% of the row and its height
       follows from the aspect. */
    flex: 0 0 var(--mm-frame-share, 40%);
    width: var(--mm-frame-share, 40%);
  }

  .mm-tile__body {
    flex: 1 1 auto;
    min-width: 0;
    /* Top-aligned beside the image (client — caption should sit at the top
       of the row, not vertically centred). flex-start inherits from the
       parent align-items but set explicitly for clarity. */
    align-self: flex-start;
  }
}

.mm-tile {
  /* A column: image frame on top, header + text beneath. */
  display: flex;
  flex-direction: column;
  min-height: 0;
  color: inherit;
  text-decoration: none;
}

.mm-tile__frame {
  /* A fixed-ratio box: width comes from the grid track, height from aspect-ratio
     (bound inline from the preset). object-fit:cover on the image fills it at
     any source shape, so every tile in the pane is the identical size and
     on-ratio no matter what was uploaded. */
  position: relative;
  flex: 0 0 auto;
  width: 100%;
  height: auto;
  overflow: hidden;
  background-color: #f3f3f3;
}

.mm-tile__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 50%;
}

/* The placeholder graphic is shown whole, not cropped to fill. */
.mm-tile__img--placeholder {
  object-fit: contain;
  background-color: #f3f3f3;
}

.mm-tile__body {
  /* Reserve the caption area even when the tile has no header and no text, so an
     empty tile's picture stays the same height as its neighbours' instead of
     shifting. Default reserves one header line + up to two text lines
     (BODY_RESERVE 48). 1-col multi-tile presets override via --mm-body-min
     from gridStyle so the row budget frees up for wider frames. */
  flex: 0 0 auto;
  min-width: 0;
  min-height: var(--mm-body-min, 48px);
  overflow: hidden;
  box-sizing: border-box;
}

/* Inline tiles (0-column presets) put the caption beside the image, centred, so
   there is no picture-height to keep in line — drop the reserved block there. */
.mm-tile--inline .mm-tile__body {
  min-height: 0;
}

.mm-tile__header {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 13px;
  font-weight: bold;
  line-height: 1.1;
  color: #383838;
  /* Reserve one line so a blank header (kept for row alignment) still holds its
     space, the way a blank column heading does. */
  min-height: 15px;
  /* Caption sits just under the image; the text hugs the header so the two read
     as one label for the image above (client review). */
  margin: 5px 0 0;
  /* Left-aligned like the description (client). Break long/unbroken tokens so
     a wide caption cannot bleed past the tile edge into the neighbouring
     column. */
  text-align: left;
  overflow-wrap: break-word;
  word-break: break-word;
}

.mm-tile__text {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 13px;
  /* Above the 13px font: a 12px line-height made a wrapped second line overlap
     the first (client QA). Kept tight, just clear of collision. */
  line-height: 15px;
  color: #383838;
  margin: 5px 0 0 0;
  /* Left-aligned, not justified (client). */
  text-align: left;
  /* Image Text is a plain textarea — never v-html. */
  white-space: normal;
  /* Break long/unbroken tokens so the description cannot spill past the tile
     edge into the next column (client QA, iPad Pro 1366). */
  overflow-wrap: break-word;
  word-break: break-word;
  /* The full caption must always be visible — never clamped to an ellipsis
     (client). A long caption grows the tile; the pane scrolls if the total
     content genuinely exceeds its height, which is the expected fallback. */
}

@media (min-width: 1280px) {
  /* Title: single line with ellipsis. Drop the reserved min-height so the
     clamped line does not push extra vertical space into the tile body. */
  .mm-tile__header {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: auto;
  }

  /* Inline tile: title and description wrap freely, then fitInlineCaptions
     clamps them so the caption never grows past the image height; whatever
     does not fit ends in an ellipsis. */
  .mm-tile--fit .mm-tile__header,
  .mm-tile--fit .mm-tile__text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Description: clamp to 2 lines with ellipsis. Full text is accessible
     via the native browser tooltip (title attribute on the element). */
  .mm-tile__text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
  }
}
</style>
